import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/Button';

import type { PackageIndexHealth, PackagePauseKey } from '../types';

/** Registries automatic indexing reaches (version-watch, auto-index on a miss). */
const PAUSABLE_REGISTRIES = ['github', 'gitlab', 'npm', 'pypi', 'pub', 'crates', 'maven'] as const;

const rowKey = (r: { registry: string; name: string; org_id?: string | null }) =>
  `${r.org_id ?? 'public'}:${r.registry}:${r.name.toLowerCase()}`;

function Status({ row }: { row: PackageIndexHealth }) {
  if (row.paused_at) {
    const by = row.paused_by === 'auto' ? 'automatically' : `by ${row.paused_by}`;
    return (
      <span title={`Since ${new Date(row.paused_at).toLocaleString()}`}>
        <span className="rounded bg-warn/15 px-1.5 py-0.5 text-3xs font-medium text-warn">
          paused
        </span>{' '}
        <span className="text-dim">{by}</span>
      </span>
    );
  }
  return (
    <span className="text-del">
      {row.consecutive_failures} failed or stalled run{row.consecutive_failures === 1 ? '' : 's'} in
      a row
    </span>
  );
}

/**
 * Packages whose automatic indexing is paused, and ones heading there (a
 * failure streak). A paused package is skipped by version-watch, auto-index on
 * a query miss and the stall sweep; an admin's own reindex still runs. The
 * form pauses any package by name — including one that has never finished
 * indexing and so has no card in the list below.
 */
export function PackageHealthPanel({
  rows,
  error,
  onPause,
  onResume,
}: {
  rows: PackageIndexHealth[] | null;
  error: string | null;
  onPause: (key: PackagePauseKey, reason: string) => Promise<void>;
  onResume: (key: PackagePauseKey) => Promise<void>;
}) {
  const [registry, setRegistry] = useState<string>('github');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    setActionError(null);
    try {
      await action();
      return true;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const ok = await run('form', () => onPause({ registry, name: trimmed }, reason.trim()));
    if (ok) {
      setName('');
      setReason('');
    }
  };

  return (
    <section data-testid="package-health-panel" className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-ink">Paused &amp; failing packages</h2>
        <p className="mt-0.5 text-xs text-dim">
          Paused packages are skipped by version-watch, auto-index and the stall requeue; your own
          reindex still runs. Three failed or stalled runs in a row pause a package automatically.
        </p>
      </div>

      <form onSubmit={(e) => void submit(e)} className="flex flex-wrap items-center gap-2">
        <select
          value={registry}
          onChange={(e) => setRegistry(e.target.value)}
          aria-label="Registry"
          className="rounded border border-rule bg-bg px-2 py-1 text-xs"
        >
          {PAUSABLE_REGISTRIES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={registry === 'github' || registry === 'gitlab' ? 'owner/repo' : 'package'}
          aria-label="Package name"
          className="w-48 rounded border border-rule bg-bg px-2 py-1 text-xs"
        />
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional)"
          aria-label="Pause reason"
          maxLength={500}
          className="min-w-0 flex-1 rounded border border-rule bg-bg px-2 py-1 text-xs"
        />
        <Button
          type="submit"
          variant="secondary"
          size="sm"
          disabled={!name.trim() || busy !== null}
        >
          {busy === 'form' ? 'Pausing…' : 'Pause'}
        </Button>
      </form>

      {(error || actionError) && (
        <div className="border border-del/30 bg-del/15 px-4 py-2 text-xs text-del">
          {actionError ?? error}
        </div>
      )}

      {rows === null ? (
        !error && <p className="text-xs text-dim">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-dim">No paused or failing packages.</p>
      ) : (
        <div className="sheet overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-2xs uppercase tracking-wider text-dim">
              <tr>
                <th className="px-3 py-2">Package</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Reason / last failure</th>
                <th className="px-3 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const key = rowKey(r);
                const target = { registry: r.registry, name: r.name, orgId: r.org_id };
                const detail = r.paused_at ? r.pause_reason : r.last_failure;
                return (
                  <tr key={r.id} className="border-t border-rule align-top">
                    <td className="px-3 py-2 font-mono text-ink">
                      {r.registry}:{r.name}
                      {r.org_id && (
                        <span className="ml-1 text-3xs text-dim" title={`Org ${r.org_id}`}>
                          (org)
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <Status row={r} />
                    </td>
                    <td className="max-w-md px-3 py-2 text-dim">
                      <span className="line-clamp-2" title={detail ?? undefined}>
                        {detail ?? '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={busy !== null}
                        onClick={() =>
                          void run(key, () =>
                            r.paused_at ? onResume(target) : onPause(target, '')
                          )
                        }
                      >
                        {busy === key ? '…' : r.paused_at ? 'Resume' : 'Pause'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
